import type { GuideSection } from '@/lib/data/guides/types';

import { StoneGuideCallout } from './StoneGuideCallout';
import { StoneGuideKeyFacts } from './StoneGuideKeyFacts';
import { parseGuideContent } from './guide-content-utils';

export interface StoneGuideContentProps {
  sections: GuideSection[];
}

export function StoneGuideContent({ sections }: StoneGuideContentProps) {
  return (
    <article className="space-y-16 md:space-y-20">
      {sections.map((section, sectionIndex) => (
        <section key={section.id} id={section.id} className="scroll-mt-28">
          <h2
            className="font-serif text-3xl md:text-4xl text-stone-900 italic mb-6 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {section.heading}
          </h2>

          <div data-speakable={sectionIndex === 0 ? true : undefined}>
            {parseGuideContent(section.content)}
          </div>

          {section.callout && (
            <StoneGuideCallout type={section.callout.type} text={section.callout.text} />
          )}

          {section.keyFacts && section.keyFacts.length > 0 && (
            <StoneGuideKeyFacts facts={section.keyFacts} />
          )}

          {section.table && section.table.rows.length > 0 && (
            <div className="overflow-x-auto my-8 border border-stone-200 bg-white">
              <table className="w-full text-left text-sm font-sans">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    {section.table.headers.map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-stone-500 font-medium"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {section.table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-4 py-3 text-stone-700 font-light"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
    </article>
  );
}

export default StoneGuideContent;
